// ─────────────────────────────────────────────────────────────────────────────
// CookieBanner.tsx
// Hoy Toca Recetas — Grupo Herdez
//
// Banner fijo en la parte inferior de la pantalla.
// Se muestra solo si el usuario no ha tomado una decisión aún.
// Acciones: Aceptar todo | Rechazar no esenciales | Configurar (abre modal)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import {
  Box,
  Button,
  Slide,
  Typography,
} from '@mui/material';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import { Link } from 'react-router-dom';
import { useConsent } from '../../hooks/useConsent';
import { CookieModal } from './CookieModal';

export const CookieBanner: React.FC = () => {
  const { showBanner, loading, acceptAll, rejectAll } = useConsent();
  const [modalOpen, setModalOpen] = useState(false);

  // No renderizar nada mientras se lee localStorage (evita flash)
  if (loading) return null;

  return (
    <>
      {/* ── Banner ── */}
      <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
        <Box
          role="dialog"
          aria-modal="false"
          aria-label="Aviso de cookies"
          sx={{
            position:      'fixed',
            bottom:        0,
            left:          0,
            right:         0,
            zIndex:        (theme) => theme.zIndex.snackbar + 1,
            bgcolor:       'background.paper',
            borderTop:     '1px solid',
            borderColor:   'divider',
            boxShadow:     '0 -4px 24px rgba(0,0,0,.10)',
            px:            { xs: 2, sm: 3, md: 4 },
            py:            { xs: 2, md: 2.5 },
            display:       'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems:    { md: 'center' },
            gap:           { xs: 2, md: 3 },
          }}
        >
          {/* Icono + texto */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
            <CookieOutlinedIcon
              sx={{ color: 'text.secondary', mt: '2px', flexShrink: 0 }}
              aria-hidden="true"
            />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Usamos cookies propias y de terceros para mejorar tu experiencia en{' '}
              <strong>Hoy Toca Recetas</strong>. Las técnicas son necesarias para el
              funcionamiento del sitio; las analíticas y de marketing son opcionales.{' '}
              Puedes aceptar, rechazar o configurarlas individualmente.{' '}
              <Link
                to="/Privacy"
                style={{ color: 'inherit', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Aviso de Privacidad
              </Link>
            </Typography>
          </Box>

          {/* Botones de acción */}
          <Box
            sx={{
              display:    'flex',
              gap:        1,
              flexShrink: 0,
              flexWrap:   'wrap',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              size="small"
              variant="text"
              onClick={() => setModalOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Configurar
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={rejectAll}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Rechazar no esenciales
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={acceptAll}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Aceptar todas
            </Button>
          </Box>
        </Box>
      </Slide>

      {/* ── Modal de preferencias (abre desde "Configurar") ── */}
      <CookieModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
